import React from "react";
import { Badge, Table } from "reactstrap";
import { formatMoney } from "../../../utils/formatMoney";
import { STATUS_META } from "./glUtils";

const CHECK_LABELS = {
  balance_chain: "Running balance chain",
  closing_balance: "Opening + credits − debits = closing",
  last_row_balance: "Last row balance = closing",
  footer_total_debit: "Footer total debit",
  footer_total_credit: "Footer total credit",
};

export const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, color: "secondary" };
  return <Badge color={meta.color}>{meta.label}</Badge>;
};

export default function StatementReport({ statement }) {
  if (!statement) return null;
  const report = statement.report || {};
  const checks = report.reconciliation?.checks || [];
  const failedChecks = checks.filter((c) => !c.ok);
  const currency = statement.currency_code;

  return (
    <div className="gl-report">
      <div className="gl-report-summary">
        <div>
          <small>Status</small>
          <StatusBadge status={statement.status} />
        </div>
        <div>
          <small>Period</small>
          <span>{statement.period_start || "?"} → {statement.period_end || "?"}</span>
        </div>
        <div>
          <small>Rows</small>
          <span>
            {statement.row_count ?? 0}
            {report.duplicate_rows ? ` (${report.duplicate_rows} duplicates skipped)` : ""}
          </span>
        </div>
        <div>
          <small>Opening</small>
          <span>{formatMoney(statement.opening_balance, currency)}</span>
        </div>
        <div>
          <small>Debit / Credit</small>
          <span>
            {formatMoney(statement.total_debit, currency)} / {formatMoney(statement.total_credit, currency)}
          </span>
        </div>
        <div>
          <small>Closing</small>
          <span>{formatMoney(statement.closing_balance, currency)}</span>
        </div>
      </div>

      {statement.error && <div className="alert alert-danger mt-3 mb-0">{statement.error}</div>}

      {statement.status === "mismatch" && (
        <div className="alert alert-warning mt-3 mb-0">
          The totals of this statement do not add up
          {failedChecks.length > 0 ? ` (${failedChecks.map((c) => CHECK_LABELS[c.key] || c.key).join(", ")})` : ""}. Its rows
          were imported but stay unlocked; compare them with the PDF before relying on them.
        </div>
      )}

      {checks.length > 0 && (
        <Table size="sm" className="mt-3 mb-0 gl-checks">
          <tbody>
            {checks.map((c) => (
              <tr key={c.key}>
                <td>{CHECK_LABELS[c.key] || c.key}</td>
                <td className="text-end">
                  {c.ok ? (
                    <Badge color="success">OK</Badge>
                  ) : (
                    <Badge color="danger" title={`Expected ${c.expected}, got ${c.actual}`}>
                      Expected {c.expected}, got {c.actual}
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {(report.warnings || []).length > 0 && (
        <ul className="gl-warnings mt-3 mb-0">
          {report.warnings.slice(0, 20).map((w, i) => (
            <li key={i}>{typeof w === "string" ? w : w.message || JSON.stringify(w)}</li>
          ))}
          {report.warnings.length > 20 && <li>… and {report.warnings.length - 20} more</li>}
        </ul>
      )}
    </div>
  );
}
